import { Navigate, useLocation } from 'react-router-dom';

import { useGetMeQuery } from '../store/auth-api.js';
import { loginPath } from './login-path.js';

/**
 * Gates the app on a live session.
 *
 * `/auth/me` is the probe: a 401 means no session. The api layer already
 * attempts one silent refresh before that 401 surfaces, so reaching here means
 * refresh was tried and failed.
 *
 * The current location is carried into the login URL so the user returns to where
 * they were — S02's "a failed refresh re-authenticates and preserves the
 * destination".
 */
export function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { data, isLoading, isError } = useGetMeQuery();
  const location = useLocation();

  if (isLoading) {
    // Deliberately plain: a skeleton of the shell would flash a layout the user
    // may not be entitled to see.
    return (
      <div
        style={{ display: 'grid', placeItems: 'center', minHeight: '100%', color: 'var(--muted)' }}
      >
        Checking your session…
      </div>
    );
  }

  if (isError || !data) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={loginPath(returnTo)} replace />;
  }

  return children;
}
